/* eslint-disable func-names */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable dot-notation */
import { ANALYTICS_TYPE, LAYER_PLACEMENT, VIEW } from '../../enums';
import {
  BannerContainer,
  BannerWrapper,
  CenterContainer,
  ColumnContainer,
  Container,
  FadingWrapper,
  RowContainer,
  SideContainer,
} from './MainLayout.styles';
import { CENTER_CONTAINER_ID, HEADING_TITLE_ID, ROOT_ID, SIDE_CONTAINER_ID } from '../../constants';
import { CSS_SELECTOR, useCustomCss } from '../../contexts/CustomCss';
import { Fragment, FunctionalComponent, RefObject, h } from 'preact';
import { SECOND_LAYER_SIDE, isPrivacyButtonUI } from '@usercentrics/cmp-browser-sdk';
import { useDeviceState, useKeyPress } from '../../hooks';
import { useEffect, useRef } from 'preact/hooks';

import AnchorList from '../AnchorList';
import ButtonsContainer from '../ButtonsContainer/ButtonsContainer';
import { ButtonsContainerProps } from '../ButtonsContainer/interface';
import CNILHeader from '../CNILHeader';
import CustomizeAnchor from '../CustomizeAnchor/CustomizeAnchor';
import Footer from '../Footer/Footer';
import Header from '../Header';
import { HeaderWrapper } from '../Header/Header.styles';
import Overlay from '../Overlay/Overlay';
import PoweredBy from '../PoweredBy';
import ShowMore from '../ShowMore/ShowMore';
import SubServiceDetail from '../SubServices/SubServiceDetail';
import { SubServicesWrapper } from '../SubServices/SubServices.styles';
import VerticalScroller from '../VerticalScroller/VerticalScroller';
import { VerticalSpacer } from '../Spacers/VerticalSpacer';
import { memo } from 'preact/compat';
import { useCategoryContext } from '../../contexts/CategoryContext';
import { useGlobalStateContext } from '../../contexts/GlobalContext';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useSubService } from '../../hooks/useSubService';
import { useUiContext } from '../../contexts/UiContext';

export type LayerSettings = {
  isOverlayEnabled: boolean;
  layerPlacement: LAYER_PLACEMENT | null;
  minVerticalScrollerHeight?: number;
  placeLinksInFooter?: boolean;
  showBorderBottom?: boolean;
  side?: SECOND_LAYER_SIDE;
  testId: string;
  withFadingBottom?: boolean;
  withFadingTop?: boolean;
  withInitialHeight?: boolean;
};

type Props = {
  cnilHeader?: { denyLinkText: string; showDenyLink: boolean; onDenyAllHandler: () => void };
  customActions?: { ariaLabel: string; label: string; onClick: () => void; elRef?: RefObject<HTMLButtonElement> }[];
  footer: {
    buttons: ButtonsContainerProps;
    showBorderBeforeFooter?: boolean;
    showSecondLayerButtonRef?: RefObject<HTMLButtonElement>;
  };
  header: {
    description: string;
    labelShowLess?: string;
    labelShowMore?: string;
    maxDescriptionHeight?: number;
    showCloseButton?: boolean;
    shortDescription?: string;
    showFullScreenButton?: boolean;
    showLanguageSelector?: boolean;
    showMoreEnabled?: boolean;
    title: string;
    functions: {
      onClose?: () => void;
      onFullScreenAmp?: () => void;
      onLanguageChange: (selectedLanguage: string) => void;
    };
    enableHTMLTextFormatting: boolean;
  };
  layerSettings: LayerSettings;
  useBackgroundColor: boolean;
};

const MainLayout: FunctionalComponent<Props> = memo(
  ({ children, cnilHeader = null, customActions, footer, header, layerSettings, useBackgroundColor = false }) => {
    const { core, labels, ui } = useSettingsContext();
    const { isAmpEnabled, view, firstLayerVariant } = useUiContext();
    const { isDesktop, isMobile, isPortrait, isLandscape } = useDeviceState();
    const mainElementRef = useRef<HTMLDivElement>(null);
    const { rules } = useCustomCss(CSS_SELECTOR.LAYER);
    const isEnterKeyPressed = useKeyPress({ targetKey: 'Enter' });
    const { blockFocus, consumeNextFocus, nextFocus, setNextFocus } = useGlobalStateContext();
    const { onCloseSubServiceDetail } = useSubService();
    const { selectedSubService } = useCategoryContext();
    const subserviceRef = useRef<HTMLDivElement>(null);

    // This makes sure that when we click on ENTER, the active element will be saved to be focused
    // once we get back to the first layer
    useEffect(() => {
      if (isEnterKeyPressed && view === VIEW.FIRST_LAYER) {
        let focusedElement = null;
        try {
          focusedElement = document.activeElement?.shadowRoot?.querySelector(':focus-visible');
        } catch (error) {
          focusedElement = null;
        }

        if (focusedElement) {
          const elAttribute = (focusedElement as HTMLElement).getAttribute('data-testid');

          if (elAttribute !== null) {
            setNextFocus(elAttribute);
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnterKeyPressed]);

    // This fixes the issue when there is a smart app banner being displayed on Safari
    useEffect(() => {
      const isSafari = (function (p) {
        return p.toString() === '[object SafariRemoteNotification]';
        // @ts-ignore
      })(!window['safari'] || (typeof window['safari'] !== 'undefined' && window['safari'].pushNotification));

      const documentHeight =
        document.documentElement.clientHeight <= document.body.clientHeight
          ? document.documentElement.clientHeight
          : document.body.clientHeight;

      const updatePositionOfCMPContainer = () => {
        if (isSafari && documentHeight > window.innerHeight && !document.hidden && isMobile) {
          const root = document.getElementById(ROOT_ID);
          const container = root?.shadowRoot?.querySelector<HTMLElement>(`#${CENTER_CONTAINER_ID}`) || null;

          if (container !== null) {
            container.style.bottom = `${documentHeight - window.innerHeight}px`;
          }
        }
      };

      updatePositionOfCMPContainer();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (typeof nextFocus === 'string') {
        consumeNextFocus();

        return;
      }

      if (nextFocus !== null) {
        consumeNextFocus();

        return;
      }

      if (!blockFocus && mainElementRef.current && !nextFocus) {
        mainElementRef.current.focus({ preventScroll: true });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customActions, footer.showSecondLayerButtonRef, nextFocus, view]);

    if (!core || !ui || !labels) {
      return null;
    }

    const buttonComponent = <ButtonsContainer {...footer.buttons} />;

    const poweredByComponent = <PoweredBy sidePosition={layerSettings.placeLinksInFooter} />;

    const linkComponent = isPrivacyButtonUI(ui) ? null : (
      <AnchorList
        links={[
          {
            ariaLabel: labels.links.privacyPolicy.ariaLabel,
            label: labels.links.privacyPolicy.label,
            type: ANALYTICS_TYPE.PRIVACY_POLICY_LINK,
            url: ui.links.privacyPolicyUrl,
          },
          {
            ariaLabel: labels.links.cookiePolicy.ariaLabel,
            label: labels.links.cookiePolicy.label,
            type: ANALYTICS_TYPE.COOKIE_POLICY_LINK,
            url: ui.links.cookiePolicyUrl,
          },
          {
            ariaLabel: labels.links.imprint.ariaLabel,
            label: labels.links.imprint.label,
            type: ANALYTICS_TYPE.IMPRINT_LINK,
            url: ui.links.imprintUrl,
          },
        ]}
        withoutMargin
      >
        <Fragment>
          {customActions &&
            customActions.map((action) => (
              <CustomizeAnchor
                ariaLabel={action.ariaLabel}
                label={action.label}
                onClick={action.onClick}
                elRef={action.elRef}
              />
            ))}
        </Fragment>
      </AnchorList>
    );

    const position = view === VIEW.FIRST_LAYER && !isPrivacyButtonUI(ui) ? ui.logo.position : 'left';

    const headerComponent = isPrivacyButtonUI(ui) ? null : (
      <HeaderWrapper view={view} firstLayerVariant={firstLayerVariant}>
        <Header
          closeAriaLabel={labels.ariaLabels.closeButton}
          fullScreenAriaLabel={labels.ariaLabels.fullscreenButton}
          onClose={header.functions.onClose}
          logo={{ ...ui.logo, position }}
          onFullScreen={header.functions.onFullScreenAmp}
          onLanguageChange={header.functions.onLanguageChange}
          showCloseButton={header.showCloseButton && !selectedSubService}
          showFullScreenButton={header.showFullScreenButton}
          showLanguage={header.showLanguageSelector && core.language.available.length > 1}
          title={header.title}
        >
          <ShowMore
            enableHTMLTextFormatting={header.enableHTMLTextFormatting}
            isShowMoreEnabled={header.showMoreEnabled}
            longDescription={header.description}
            maxDescriptionHeight={header.maxDescriptionHeight}
            shortDescription={header.shortDescription}
            labelShowLess={header.labelShowLess}
            labelShowMore={header.labelShowMore}
            withFading
          />

          {!layerSettings.placeLinksInFooter && (
            <Fragment>
              <VerticalSpacer small />
              {linkComponent}
            </Fragment>
          )}
        </Header>
      </HeaderWrapper>
    );

    const footerComponent = (
      <Footer showBorder={footer.showBorderBeforeFooter}>
        {layerSettings.placeLinksInFooter ? (
          <RowContainer>
            <ColumnContainer>
              {linkComponent}
              {(isDesktop || isLandscape) && poweredByComponent}
            </ColumnContainer>

            {buttonComponent}
            {isMobile && isPortrait && poweredByComponent}
          </RowContainer>
        ) : (
          <Fragment>
            {buttonComponent}
            {poweredByComponent}
          </Fragment>
        )}
      </Footer>
    );

    const mainElement = (
      <Fragment>
        <Container data-testid="uc-container">
          <FadingWrapper
            id="uc-fading-wrapper"
            isMobile={isMobile}
            withFadingBottom={layerSettings.withFadingBottom}
            withFadingTop={layerSettings.withFadingTop}
            useBackgroundColor={useBackgroundColor}
          >
            {cnilHeader?.showDenyLink && (
              <CNILHeader
                denyLinkText={cnilHeader.denyLinkText}
                onDenyAllHandler={() => cnilHeader.onDenyAllHandler()}
              />
            )}
            <VerticalScroller
              minHeight={layerSettings.minVerticalScrollerHeight}
              useBackgroundColor={useBackgroundColor}
              layerPlacement={layerSettings.layerPlacement}
            >
              {headerComponent}
              {children}
            </VerticalScroller>
            {selectedSubService && (
              <Fragment>
                <SubServicesWrapper ref={subserviceRef} tabIndex={0}>
                  {headerComponent}
                  <SubServiceDetail
                    subService={selectedSubService}
                    onCloseDetail={() => {
                      onCloseSubServiceDetail();
                    }}
                  />
                </SubServicesWrapper>
              </Fragment>
            )}
          </FadingWrapper>
        </Container>
        {footerComponent}
      </Fragment>
    );

    switch (layerSettings.layerPlacement) {
      case LAYER_PLACEMENT.CENTER:
        return (
          <Fragment>
            {layerSettings.isOverlayEnabled && !isAmpEnabled && <Overlay />}
            <CenterContainer
              lang={core.language.selected}
              role="dialog"
              aria-modal
              aria-labelledby={HEADING_TITLE_ID}
              tabIndex={0}
              ref={mainElementRef}
              id={CENTER_CONTAINER_ID}
              data-testid={layerSettings.testId}
              isAmpEnabled={isAmpEnabled}
              showBorderBottom={layerSettings.showBorderBottom || false}
              withInitialHeight={layerSettings.withInitialHeight || false}
              style={{ ...rules }}
            >
              {mainElement}
            </CenterContainer>
          </Fragment>
        );
      case LAYER_PLACEMENT.SIDE:
        return (
          <Fragment>
            {layerSettings.isOverlayEnabled && !isAmpEnabled && <Overlay />}
            <SideContainer
              lang={core.language.selected}
              role="dialog"
              aria-modal
              aria-labelledby={HEADING_TITLE_ID}
              tabIndex={0}
              ref={mainElementRef}
              id={SIDE_CONTAINER_ID}
              data-testid={layerSettings.testId}
              privacyButtonPosition={isPrivacyButtonUI(ui) ? ui.position : ''}
              showBorderBottom={layerSettings.showBorderBottom || false}
              side={layerSettings.side}
              style={{ ...rules }}
            >
              {mainElement}
            </SideContainer>
          </Fragment>
        );

      case LAYER_PLACEMENT.TOP:
      case LAYER_PLACEMENT.BOTTOM:
        return (
          <Fragment>
            {layerSettings.isOverlayEnabled && !isAmpEnabled && <Overlay />}
            <BannerWrapper
              ref={mainElementRef}
              showBorderBottom={layerSettings.showBorderBottom || false}
              layerPlacement={layerSettings.layerPlacement}
              isAmpEnabled={isAmpEnabled}
            >
              <BannerContainer
                lang={core.language.selected}
                tabIndex={0}
                data-testid={layerSettings.testId}
                showBorderBottom={layerSettings.showBorderBottom || false}
                layerPlacement={layerSettings.layerPlacement}
                isAmpEnabled={isAmpEnabled}
                style={{ ...rules }}
              >
                {mainElement}
              </BannerContainer>
            </BannerWrapper>
          </Fragment>
        );
      default:
        return <Fragment />;
    }
  },
);

export default MainLayout;
